import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChildren,
} from '@angular/core';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ColorPickerModule } from 'primeng/colorpicker';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { Popover, PopoverModule } from 'primeng/popover';
import {
  resolveSettingsLoadErrorDetails,
  SettingsCard,
  SettingsCardActions,
  SettingsCardNew,
  SettingsActions,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';
import {
  type ActivateFrontendTheme,
  FrontendThemeApi,
  FrontendThemeConfiguration,
  FrontendThemeItem,
  FrontendThemeMode,
  PatchFrontendThemeConfiguration,
  PatchFrontendThemeMode,
} from '@tourfold/libraries/tf-api-client-frontend-v2';
import { Brand, BrandsApi } from '@tourfold/libraries/tf-api-client-public-v2';
import { CloseOverlayOnNavigationDirective } from '@tourfold/libraries/shared-ui';
import { ShellHeaderService } from '@tourfold/libraries/shared-ui/page-header/shell-header.service';
import { FrontendConfigurationService } from '@tourfold/shared-kernels/frontend-configuration';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';
import { ThemePreviewComponent, ThemePreviewMode } from './theme-preview/theme-preview.component';
import {
  LinkedThemeBrand,
  ThemeLinkedBrandsPopoverComponent,
} from './theme-linked-brands-popover/theme-linked-brands-popover.component';
import { provideThemeSettingsAiPageContext } from './settings-theme.ai-page-context';

type ThemeModeKey = 'light' | 'dark';
type ThemeEditorMode = 'edit' | 'new';

interface ColorField {
  key: keyof FrontendThemeMode | 'header' | 'surface' | 'sidebar';
  nestedKey?: string;
  labelKey: string;
  descriptionKey: string;
}

interface FontStyleOption {
  label: string;
  value: 'sora' | 'hind_madurai';
}

const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

const COLOR_FIELDS: ColorField[] = [
  {
    key: 'primaryColor',
    labelKey: 'SETTINGS_THEME.FIELDS.PRIMARY_COLOR',
    descriptionKey: 'SETTINGS_THEME.FIELDS.PRIMARY_COLOR_DESC',
  },
  {
    key: 'accentColor',
    labelKey: 'SETTINGS_THEME.FIELDS.ACCENT_COLOR',
    descriptionKey: 'SETTINGS_THEME.FIELDS.ACCENT_COLOR_DESC',
  },
  {
    key: 'textColor',
    labelKey: 'SETTINGS_THEME.FIELDS.TEXT_COLOR',
    descriptionKey: 'SETTINGS_THEME.FIELDS.TEXT_COLOR_DESC',
  },
  {
    key: 'textMutedColor',
    labelKey: 'SETTINGS_THEME.FIELDS.TEXT_MUTED_COLOR',
    descriptionKey: 'SETTINGS_THEME.FIELDS.TEXT_MUTED_COLOR_DESC',
  },
  {
    key: 'borderColor',
    labelKey: 'SETTINGS_THEME.FIELDS.BORDER_COLOR',
    descriptionKey: 'SETTINGS_THEME.FIELDS.BORDER_COLOR_DESC',
  },
  {
    key: 'borderStrongColor',
    labelKey: 'SETTINGS_THEME.FIELDS.BORDER_STRONG_COLOR',
    descriptionKey: 'SETTINGS_THEME.FIELDS.BORDER_STRONG_COLOR_DESC',
  },
];

const SURFACE_COLOR_FIELDS: ColorField[] = [
  {
    key: 'surface',
    nestedKey: 'pageBackgroundColor',
    labelKey: 'SETTINGS_THEME.FIELDS.PAGE_BACKGROUND_COLOR',
    descriptionKey: 'SETTINGS_THEME.FIELDS.PAGE_BACKGROUND_COLOR_DESC',
  },
  {
    key: 'surface',
    nestedKey: 'publicPageBackgroundColor',
    labelKey: 'SETTINGS_THEME.FIELDS.PUBLIC_PAGE_BACKGROUND_COLOR',
    descriptionKey: 'SETTINGS_THEME.FIELDS.PUBLIC_PAGE_BACKGROUND_COLOR_DESC',
  },
  {
    key: 'surface',
    nestedKey: 'panelBackgroundColor',
    labelKey: 'SETTINGS_THEME.FIELDS.PANEL_BACKGROUND_COLOR',
    descriptionKey: 'SETTINGS_THEME.FIELDS.PANEL_BACKGROUND_COLOR_DESC',
  },
  {
    key: 'surface',
    nestedKey: 'barBackgroundColor',
    labelKey: 'SETTINGS_THEME.FIELDS.BAR_BACKGROUND_COLOR',
    descriptionKey: 'SETTINGS_THEME.FIELDS.BAR_BACKGROUND_COLOR_DESC',
  },
  {
    key: 'surface',
    nestedKey: 'listBackgroundColor',
    labelKey: 'SETTINGS_THEME.FIELDS.LIST_BACKGROUND_COLOR',
    descriptionKey: 'SETTINGS_THEME.FIELDS.LIST_BACKGROUND_COLOR_DESC',
  },
  {
    key: 'surface',
    nestedKey: 'inputBackgroundColor',
    labelKey: 'SETTINGS_THEME.FIELDS.INPUT_BACKGROUND_COLOR',
    descriptionKey: 'SETTINGS_THEME.FIELDS.INPUT_BACKGROUND_COLOR_DESC',
  },
  {
    key: 'surface',
    nestedKey: 'hoverBackgroundColor',
    labelKey: 'SETTINGS_THEME.FIELDS.HOVER_BACKGROUND_COLOR',
    descriptionKey: 'SETTINGS_THEME.FIELDS.HOVER_BACKGROUND_COLOR_DESC',
  },
  {
    key: 'surface',
    nestedKey: 'selectedBackgroundColor',
    labelKey: 'SETTINGS_THEME.FIELDS.SELECTED_BACKGROUND_COLOR',
    descriptionKey: 'SETTINGS_THEME.FIELDS.SELECTED_BACKGROUND_COLOR_DESC',
  },
];

const HEADER_COLOR_FIELDS: ColorField[] = [
  {
    key: 'header',
    nestedKey: 'backgroundColor',
    labelKey: 'SETTINGS_THEME.FIELDS.HEADER_BG',
    descriptionKey: 'SETTINGS_THEME.FIELDS.HEADER_BG_DESC',
  },
  {
    key: 'header',
    nestedKey: 'foregroundColor',
    labelKey: 'SETTINGS_THEME.FIELDS.HEADER_FG',
    descriptionKey: 'SETTINGS_THEME.FIELDS.HEADER_FG_DESC',
  },
];

const SIDEBAR_COLOR_FIELDS: ColorField[] = [
  {
    key: 'sidebar',
    nestedKey: 'backgroundColor',
    labelKey: 'SETTINGS_THEME.FIELDS.SIDEBAR_BG',
    descriptionKey: 'SETTINGS_THEME.FIELDS.SIDEBAR_BG_DESC',
  },
  {
    key: 'sidebar',
    nestedKey: 'foregroundColor',
    labelKey: 'SETTINGS_THEME.FIELDS.SIDEBAR_FG',
    descriptionKey: 'SETTINGS_THEME.FIELDS.SIDEBAR_FG_DESC',
  },
  {
    key: 'sidebar',
    nestedKey: 'headerBackgroundColor',
    labelKey: 'SETTINGS_THEME.FIELDS.SIDEBAR_HEADER_BG',
    descriptionKey: 'SETTINGS_THEME.FIELDS.SIDEBAR_HEADER_BG_DESC',
  },
  {
    key: 'sidebar',
    nestedKey: 'headerForegroundColor',
    labelKey: 'SETTINGS_THEME.FIELDS.SIDEBAR_HEADER_FG',
    descriptionKey: 'SETTINGS_THEME.FIELDS.SIDEBAR_HEADER_FG_DESC',
  },
  {
    key: 'sidebar',
    nestedKey: 'activeBackgroundColor',
    labelKey: 'SETTINGS_THEME.FIELDS.SIDEBAR_ACTIVE_BG',
    descriptionKey: 'SETTINGS_THEME.FIELDS.SIDEBAR_ACTIVE_BG_DESC',
  },
  {
    key: 'sidebar',
    nestedKey: 'activeForegroundColor',
    labelKey: 'SETTINGS_THEME.FIELDS.SIDEBAR_ACTIVE_FG',
    descriptionKey: 'SETTINGS_THEME.FIELDS.SIDEBAR_ACTIVE_FG_DESC',
  },
  {
    key: 'sidebar',
    nestedKey: 'activeIndicatorColor',
    labelKey: 'SETTINGS_THEME.FIELDS.SIDEBAR_ACTIVE_INDICATOR',
    descriptionKey: 'SETTINGS_THEME.FIELDS.SIDEBAR_ACTIVE_INDICATOR_DESC',
  },
];

@Component({
  selector: 'tf-settings-page-theme',
  imports: [
    FormsModule,
    NgTemplateOutlet,
    TranslateModule,
    ButtonModule,
    ColorPickerModule,
    ConfirmDialogModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    SelectModule,
    TooltipModule,
    PopoverModule,
    SettingsCard,
    SettingsCardActions,
    SettingsCardNew,
    SettingsActions,
    SettingsInlineMessage,
    SettingsLoadError,
    ThemeLinkedBrandsPopoverComponent,
    ThemePreviewComponent,
    CloseOverlayOnNavigationDirective,
  ],
  templateUrl: './settings-theme.component.html',
  styleUrl: './settings-theme.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class ThemeSettingsComponent implements OnInit, OnDestroy {
  private readonly themeApi = inject(FrontendThemeApi);
  private readonly brandsApi = inject(BrandsApi);
  private readonly tenantConfigService = inject(FrontendConfigurationService);
  private readonly resourceAccessService = inject(ResourceAccessService);
  private readonly translateService = inject(TranslateService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly shellHeader = inject(ShellHeaderService);
  private readonly document = inject(DOCUMENT);

  readonly linkedBrandsPopovers = viewChildren<Popover>('linkedBrandsPopover');

  readonly canCreateTheme = computed(() =>
    this.resourceAccessService.canPerform('theme', 'create')
  );
  readonly canUpdateTheme = computed(() =>
    this.resourceAccessService.canPerform('theme', 'update')
  );
  readonly canDeleteTheme = computed(() =>
    this.resourceAccessService.canPerform('theme', 'delete')
  );
  readonly canReadBrands = computed(() => this.resourceAccessService.canPerform('brands', 'read'));
  readonly canManageTheme = computed(
    () => this.canCreateTheme() || this.canUpdateTheme() || this.canDeleteTheme()
  );

  readonly colorFields = COLOR_FIELDS;
  readonly headerColorFields = HEADER_COLOR_FIELDS;
  readonly surfaceColorFields = SURFACE_COLOR_FIELDS;
  readonly sidebarColorFields = SIDEBAR_COLOR_FIELDS;

  readonly themes = signal<FrontendThemeItem[]>([]);
  readonly linkedBrands = signal<Brand[]>([]);
  readonly hasLoadedOnce = signal(false);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly mutatingThemeKey = signal<string | null>(null);
  readonly listError = signal<string | null>(null);
  readonly listLoadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly initialLoadFailed = signal(false);
  readonly editorError = signal<string | null>(null);
  readonly actionErrors = signal<Record<string, string>>({});
  readonly previewModes = signal<Record<string, ThemePreviewMode>>({});
  readonly skeletonCount = [0, 1, 2, 3];

  readonly editingTheme = signal<FrontendThemeItem | null>(null);
  readonly editorMode = signal<ThemeEditorMode>('edit');
  readonly editorName = signal('');
  readonly editorDescription = signal('');
  readonly serverTheme = signal<FrontendThemeConfiguration | null>(null);
  readonly lightMode = signal<FrontendThemeMode | null>(null);
  readonly darkMode = signal<FrontendThemeMode | null>(null);
  readonly activeMode = signal<ThemeModeKey>('light');
  readonly validationErrors = signal<Record<string, string>>({});

  readonly fontStyleOptions: FontStyleOption[] = [
    { label: 'Sora', value: 'sora' },
    { label: 'Hind Madurai', value: 'hind_madurai' },
  ];

  readonly activeTheme = computed(
    () =>
      this.themes().find((theme) => theme.active) ??
      this.themes().find((theme) => theme.builtIn) ??
      null
  );
  readonly showListActions = computed(() => this.hasLoadedOnce() && !this.initialLoadFailed());
  readonly defaultTheme = computed(() => this.themes().find((theme) => theme.builtIn) ?? null);
  readonly linkedBrandsByThemeId = computed<Readonly<Record<string, readonly LinkedThemeBrand[]>>>(
    () => {
      const groups: Record<string, LinkedThemeBrand[]> = {};
      for (const brand of this.linkedBrands()) {
        const themeId = brand.themeId;
        if (!themeId) {
          continue;
        }
        const group = groups[themeId] ?? [];
        group.push({
          id: brand.id,
          name: brand.name,
          logoUrl: brand.logoUrl,
        });
        groups[themeId] = group;
      }
      return groups;
    }
  );

  readonly currentMode = computed(() => {
    return this.activeMode() === 'light' ? this.lightMode() : this.darkMode();
  });

  readonly editorPreviewTheme = computed<FrontendThemeConfiguration | null>(() => {
    const light = this.lightMode();
    const dark = this.darkMode();
    if (!light || !dark) {
      return this.serverTheme();
    }
    return { light, dark };
  });

  readonly editorTitleKey = computed(() =>
    this.editorMode() === 'new' ? 'SETTINGS_THEME.EDITOR_NEW_TITLE' : 'SETTINGS_THEME.EDITOR_TITLE'
  );

  readonly hasEditorChanges = computed(() => {
    const theme = this.editingTheme();
    const serverTheme = this.serverTheme();
    const light = this.lightMode();
    const dark = this.darkMode();
    if (!theme || !serverTheme || !light || !dark) {
      return false;
    }
    return (
      this.editorName().trim() !== theme.name ||
      this.normalizeDescription(this.editorDescription()) !==
        this.normalizeDescription(theme.description) ||
      JSON.stringify(light) !== JSON.stringify(serverTheme.light) ||
      JSON.stringify(dark) !== JSON.stringify(serverTheme.dark)
    );
  });

  readonly isValid = computed(() => Object.keys(this.validationErrors()).length === 0);

  readonly isSaveDisabled = computed(() => {
    const canSave = this.editorMode() === 'new' ? this.canCreateTheme() : this.canUpdateTheme();
    return (
      !canSave ||
      (this.editorMode() !== 'new' && !this.hasEditorChanges()) ||
      !this.isValid() ||
      this.isSaving() ||
      !this.editorName().trim()
    );
  });

  constructor() {
    provideThemeSettingsAiPageContext({
      themes: this.themes,
      editingTheme: this.editingTheme,
      editorName: this.editorName,
    });

    // Drive the shell-owned header on the editor (new/edit) screens: a back action that returns to
    // the theme list. Cleared in ngOnDestroy / when leaving the editor so it never leaks back to
    // the list view. Same ShellHeaderService pattern as the object-definition detail page.
    effect(() => {
      if (this.editingTheme()) {
        this.shellHeader.configure({
          back: () => this.closeEditor(),
        });
      } else {
        this.shellHeader.reset();
      }
    });
  }

  ngOnInit(): void {
    void this.loadThemes();
  }

  ngOnDestroy(): void {
    this.closeLinkedBrandsPopovers();
    this.shellHeader.reset();
  }

  previewKey(theme: FrontendThemeItem): string {
    return theme.id ?? 'default';
  }

  previewMode(theme: FrontendThemeItem): ThemePreviewMode {
    return this.previewModes()[this.previewKey(theme)] ?? 'light';
  }

  setPreviewMode(theme: FrontendThemeItem, mode: ThemePreviewMode): void {
    const key = this.previewKey(theme);
    this.previewModes.update((modes) => ({ ...modes, [key]: mode }));
  }

  togglePreviewMode(theme: FrontendThemeItem): void {
    this.setPreviewMode(theme, this.previewMode(theme) === 'light' ? 'dark' : 'light');
  }

  reload(): void {
    this.closeLinkedBrandsPopovers();
    void this.loadThemes();
  }

  actionError(theme: FrontendThemeItem): string | null {
    return this.actionErrors()[this.previewKey(theme)] ?? null;
  }

  clearListError(): void {
    this.listError.set(null);
  }

  openEditor(theme: FrontendThemeItem, mode: ThemeEditorMode = 'edit'): void {
    this.closeLinkedBrandsPopovers();
    if (theme.builtIn) {
      void this.duplicateTheme(theme);
      return;
    }

    this.editingTheme.set(theme);
    this.editorMode.set(mode);
    this.editorName.set(theme.name);
    this.editorDescription.set(theme.description ?? '');
    this.serverTheme.set(this.cloneTheme(theme.theme));
    this.lightMode.set(this.cloneMode(theme.theme.light));
    this.darkMode.set(this.cloneMode(theme.theme.dark));
    this.activeMode.set('light');
    this.validationErrors.set({});
    this.editorError.set(null);
  }

  closeEditor(): void {
    this.editingTheme.set(null);
    this.editorMode.set('edit');
    this.editorName.set('');
    this.editorDescription.set('');
    this.serverTheme.set(null);
    this.lightMode.set(null);
    this.darkMode.set(null);
    this.validationErrors.set({});
    this.editorError.set(null);
  }

  discardChanges(): void {
    const theme = this.editingTheme();
    if (!theme) {
      return;
    }
    this.openEditor(theme, this.editorMode());
  }

  async loadThemes(): Promise<void> {
    const hadLoadedOnce = this.hasLoadedOnce();
    this.isLoading.set(true);
    try {
      await this.refreshThemes();
      this.listError.set(null);
      this.listLoadErrorDetails.set(null);
      this.initialLoadFailed.set(false);
      this.hasLoadedOnce.set(true);
    } catch (error: unknown) {
      this.listError.set(this.translateService.instant('SETTINGS_THEME.ERROR.LOAD_FAILED'));
      if (!hadLoadedOnce) {
        this.initialLoadFailed.set(true);
        this.listLoadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
      } else {
        this.hasLoadedOnce.set(true);
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  async createTheme(): Promise<void> {
    this.closeLinkedBrandsPopovers();
    if (this.initialLoadFailed() || !this.canCreateTheme()) {
      return;
    }
    const source = this.defaultTheme() ?? this.themes()[0];
    if (!source) {
      return;
    }

    this.listError.set(null);
    this.openEditor(
      this.createDraftTheme({
        source,
        name: this.translateService.instant('SETTINGS_THEME.DEFAULT_NEW_NAME'),
      }),
      'new'
    );
  }

  duplicateTheme(theme: FrontendThemeItem): void {
    this.closeLinkedBrandsPopovers();
    if (!this.canCreateTheme()) {
      return;
    }
    this.clearActionError(theme);
    this.openEditor(
      this.createDraftTheme({
        source: theme,
        name: this.translateService.instant('SETTINGS_THEME.COPY_NAME', { name: theme.name }),
        description: theme.description,
      }),
      'new'
    );
  }

  async activateTheme(theme: FrontendThemeItem): Promise<void> {
    this.closeLinkedBrandsPopovers();
    if (!this.canUpdateTheme() || theme.active) {
      return;
    }
    this.mutatingThemeKey.set(this.previewKey(theme));
    this.clearActionError(theme);
    try {
      // The API uses null as the explicit built-in default theme id.
      const activateFrontendTheme = {
        themeId: theme.id ?? null,
      } as unknown as ActivateFrontendTheme;
      const activated = await this.themeApi.activateFrontendTheme({
        activateFrontendTheme,
      });
      this.themes.update((themes) =>
        themes.map((item) => ({
          ...item,
          active: this.previewKey(item) === this.previewKey(activated),
        }))
      );
      this.upsertTheme(activated);
      this.tenantConfigService.updateEffectiveTheme(activated.theme);
    } catch {
      this.setActionError(
        theme,
        this.translateService.instant('SETTINGS_THEME.ERROR.ACTIVATE_FAILED')
      );
    } finally {
      this.mutatingThemeKey.set(null);
    }
  }

  confirmDeleteTheme(theme: FrontendThemeItem): void {
    this.closeLinkedBrandsPopovers();
    if (theme.builtIn || theme.active || !this.canDeleteTheme()) {
      return;
    }

    const linkedNames = theme.linkedBrandNames.join(', ');
    const message =
      theme.linkedBrandCount > 0
        ? this.translateService.instant('SETTINGS_THEME.DELETE_CONFIRM_MESSAGE_LINKED', {
            name: theme.name,
            count: theme.linkedBrandCount,
            brands: linkedNames,
          })
        : this.translateService.instant('SETTINGS_THEME.DELETE_CONFIRM_MESSAGE', {
            name: theme.name,
          });

    this.confirmationService.confirm({
      header: this.translateService.instant('SETTINGS_THEME.DELETE_CONFIRM_TITLE'),
      message,
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => void this.deleteTheme(theme),
    });
  }

  async deleteTheme(theme: FrontendThemeItem): Promise<void> {
    if (theme.builtIn) {
      return;
    }
    if (!theme.id) {
      return;
    }
    this.mutatingThemeKey.set(this.previewKey(theme));
    this.clearActionError(theme);
    try {
      await this.themeApi.deleteFrontendTheme({ themeId: theme.id });
      this.themes.update((themes) => themes.filter((item) => item.id !== theme.id));
      if (this.editingTheme()?.id === theme.id) {
        this.closeEditor();
      }
    } catch {
      this.setActionError(
        theme,
        this.translateService.instant('SETTINGS_THEME.ERROR.DELETE_FAILED')
      );
    } finally {
      this.mutatingThemeKey.set(null);
    }
  }

  async saveTheme(): Promise<void> {
    const theme = this.editingTheme();
    const light = this.lightMode();
    const dark = this.darkMode();
    if (!theme || theme.builtIn || !light || !dark || !this.validateAll()) {
      return;
    }

    if (this.editorMode() === 'new') {
      await this.saveNewTheme(light, dark);
      return;
    }

    if (!theme.id) {
      return;
    }

    this.isSaving.set(true);
    this.editorError.set(null);
    try {
      const updated = await this.themeApi.patchFrontendTheme({
        themeId: theme.id,
        patchFrontendThemeItem: {
          name: this.editorName().trim(),
          description: this.descriptionForPatch(this.editorDescription()),
          lockVersion: theme.lockVersion,
          theme: this.toPatchConfiguration({ light, dark }),
        },
      });
      this.upsertTheme(updated);
      if (updated.active) {
        this.tenantConfigService.updateEffectiveTheme(updated.theme);
      }
      this.closeEditor();
    } catch (error: unknown) {
      if (this.isConflict(error)) {
        const conflictMessage = this.translateService.instant('SETTINGS_THEME.ERROR.CONFLICT');
        this.editorError.set(conflictMessage);
        try {
          await this.refreshThemes();
        } catch {
          this.editorError.set(this.translateService.instant('SETTINGS_THEME.ERROR.LOAD_FAILED'));
        }
        const fresh = this.themes().find((item) => item.id === theme.id);
        if (fresh) {
          this.openEditor(fresh, this.editorMode());
          this.editorError.set(conflictMessage);
        }
      } else {
        this.editorError.set(this.translateService.instant('SETTINGS_THEME.ERROR.SAVE_FAILED'));
      }
    } finally {
      this.isSaving.set(false);
    }
  }

  onNameChange(value: string): void {
    this.editorName.set(value);
    this.editorError.set(null);
  }

  onDescriptionChange(value: string): void {
    this.editorDescription.set(value);
    this.editorError.set(null);
  }

  onModeChange(mode: ThemeModeKey): void {
    this.activeMode.set(mode);
  }

  onColorChange(fieldKey: string, nestedKey: string | undefined, value: string): void {
    this.editorError.set(null);
    this.updateCurrentModeField(fieldKey, nestedKey, value);
    this.validateField(fieldKey, nestedKey, value);
  }

  onColorPickerChange(fieldKey: string, nestedKey: string | undefined, hexValue: string): void {
    this.editorError.set(null);
    const normalizedHex = hexValue.startsWith('#') ? hexValue : `#${hexValue}`;
    this.updateCurrentModeField(fieldKey, nestedKey, normalizedHex);
    this.validateField(fieldKey, nestedKey, normalizedHex);
  }

  onFontStyleChange(value: 'sora' | 'hind_madurai'): void {
    const current = this.currentMode();
    if (!current) {
      return;
    }
    this.editorError.set(null);
    this.setCurrentMode({ ...current, baseFontStyle: value });
  }

  getFontSizeNumeric(): string {
    return (this.currentMode()?.baseFontSize ?? '').replace('%', '');
  }

  onFontSizeChange(value: string): void {
    const current = this.currentMode();
    if (!current) {
      return;
    }
    this.editorError.set(null);
    this.setCurrentMode({ ...current, baseFontSize: value ? `${value}%` : '' });
    this.validateFontSize(value);
  }

  getFieldValue(fieldKey: string, nestedKey?: string): string {
    const current = this.currentMode();
    if (!current) {
      return '';
    }

    if (nestedKey && fieldKey === 'sidebar') {
      return current.sidebar[nestedKey as keyof typeof current.sidebar] ?? '';
    }
    if (nestedKey && fieldKey === 'header') {
      return current.header[nestedKey as keyof typeof current.header] ?? '';
    }
    if (nestedKey && fieldKey === 'surface') {
      return current.surface[nestedKey as keyof typeof current.surface] ?? '';
    }
    return (current[fieldKey as keyof FrontendThemeMode] as string) ?? '';
  }

  getColorPickerValue(fieldKey: string, nestedKey?: string): string {
    const value = this.getFieldValue(fieldKey, nestedKey);
    return HEX_COLOR_REGEX.test(value) ? value.substring(1) : '000000';
  }

  getFieldId(fieldKey: string, nestedKey?: string): string {
    return nestedKey
      ? `theme-${this.activeMode()}-${fieldKey}-${nestedKey}`
      : `theme-${this.activeMode()}-${fieldKey}`;
  }

  getValidationError(fieldKey: string, nestedKey?: string): string | null {
    const key = this.validationKey(fieldKey, nestedKey);
    return this.validationErrors()[key] ?? null;
  }

  linkedBrandsLabel(theme: FrontendThemeItem): string {
    const labelKey =
      theme.linkedBrandCount === 1
        ? 'SETTINGS_THEME.LINKED_BRANDS.COUNT_ONE'
        : 'SETTINGS_THEME.LINKED_BRANDS.COUNT_OTHER';
    return this.translateService.instant(labelKey, {
      count: theme.linkedBrandCount,
    });
  }

  linkedBrandsForTheme(theme: FrontendThemeItem): readonly LinkedThemeBrand[] {
    if (!theme.id) {
      return [];
    }

    const hydrated = this.linkedBrandsByThemeId()[theme.id] ?? [];
    if (hydrated.length > 0) {
      return hydrated;
    }

    return theme.linkedBrandNames.map((name, index) => ({
      id: `${theme.id}-${index}`,
      name,
    }));
  }

  toggleLinkedBrandsPopover(event: Event, popover: Popover): void {
    for (const linkedBrandsPopover of this.linkedBrandsPopovers()) {
      if (linkedBrandsPopover !== popover) {
        linkedBrandsPopover.hide();
      }
    }
    popover.toggle(event);
  }

  private closeLinkedBrandsPopovers(): void {
    for (const linkedBrandsPopover of this.linkedBrandsPopovers()) {
      linkedBrandsPopover.hide();
    }
    this.scheduleLinkedBrandsPopoverCleanup();
  }

  private scheduleLinkedBrandsPopoverCleanup(): void {
    queueMicrotask(() => this.removeLinkedBrandsPopoverOverlays());
    setTimeout(() => this.removeLinkedBrandsPopoverOverlays());
  }

  private removeLinkedBrandsPopoverOverlays(): void {
    const overlays = this.document.querySelectorAll('.p-popover');
    overlays.forEach((overlay) => {
      if (overlay.querySelector('tf-settings-theme-linked-brands-popover')) {
        overlay.remove();
      }
    });
  }

  private upsertTheme(theme: FrontendThemeItem): void {
    this.themes.update((themes) => {
      const index = themes.findIndex((item) => this.previewKey(item) === this.previewKey(theme));
      if (index === -1) {
        return [...themes, theme];
      }
      const next = [...themes];
      next[index] = theme;
      return next;
    });
  }

  private async saveNewTheme(light: FrontendThemeMode, dark: FrontendThemeMode): Promise<void> {
    if (!this.canCreateTheme()) {
      return;
    }

    this.isSaving.set(true);
    this.editorError.set(null);
    try {
      const created = await this.themeApi.createFrontendTheme({
        createFrontendTheme: {
          name: this.editorName().trim(),
          description: this.descriptionForPatch(this.editorDescription()),
          theme: this.toPatchConfiguration({ light, dark }),
          activate: false,
        },
      });
      this.upsertTheme(created);
      this.closeEditor();
    } catch {
      this.editorError.set(this.translateService.instant('SETTINGS_THEME.ERROR.CREATE_FAILED'));
    } finally {
      this.isSaving.set(false);
    }
  }

  private createDraftTheme({
    source,
    name,
    description,
  }: {
    source: FrontendThemeItem;
    name: string;
    description?: string;
  }): FrontendThemeItem {
    const now = new Date();
    return {
      id: undefined,
      name,
      description,
      theme: this.cloneTheme(source.theme),
      active: false,
      builtIn: false,
      lockVersion: 0,
      createdAt: now,
      updatedAt: now,
      linkedBrandCount: 0,
      linkedBrandNames: [],
    };
  }

  private async refreshThemes(): Promise<void> {
    const response = await this.themeApi.listFrontendThemes();
    this.themes.set(response.items);
    await this.refreshLinkedBrandsIfAvailable();
  }

  private async refreshLinkedBrandsIfAvailable(): Promise<void> {
    if (!this.canReadBrands()) {
      this.linkedBrands.set([]);
      return;
    }
    try {
      this.linkedBrands.set(await this.fetchAllBrands());
    } catch {
      this.linkedBrands.set([]);
    }
  }

  private async fetchAllBrands(): Promise<Brand[]> {
    const size = 100;
    let page = 0;
    let totalPages = 1;
    const brands: Brand[] = [];

    while (page < totalPages) {
      const response = await this.brandsApi.listBrands({ page, size });
      brands.push(...response.items);
      totalPages = response.page.totalPages;
      page += 1;
    }

    return brands;
  }

  private setActionError(theme: FrontendThemeItem, message: string): void {
    const key = this.previewKey(theme);
    this.actionErrors.update((errors) => ({ ...errors, [key]: message }));
  }

  private clearActionError(theme: FrontendThemeItem): void {
    const key = this.previewKey(theme);
    this.actionErrors.update((errors) => {
      const next = { ...errors };
      delete next[key];
      return next;
    });
  }

  private updateCurrentModeField(
    fieldKey: string,
    nestedKey: string | undefined,
    value: string
  ): void {
    const current = this.currentMode();
    if (!current) {
      return;
    }

    if (nestedKey && fieldKey === 'sidebar') {
      this.setCurrentMode({
        ...current,
        sidebar: { ...current.sidebar, [nestedKey]: value },
      });
      return;
    }
    if (nestedKey && fieldKey === 'header') {
      this.setCurrentMode({
        ...current,
        header: { ...current.header, [nestedKey]: value },
      });
      return;
    }
    if (nestedKey && fieldKey === 'surface') {
      this.setCurrentMode({
        ...current,
        surface: { ...current.surface, [nestedKey]: value },
      });
      return;
    }

    this.setCurrentMode({ ...current, [fieldKey]: value });
  }

  private setCurrentMode(mode: FrontendThemeMode): void {
    if (this.activeMode() === 'light') {
      this.lightMode.set(mode);
      return;
    }
    this.darkMode.set(mode);
  }

  private validateField(fieldKey: string, nestedKey: string | undefined, value: string): boolean {
    const key = this.validationKey(fieldKey, nestedKey);
    const errors = { ...this.validationErrors() };
    if (!value) {
      errors[key] = this.translateService.instant('SETTINGS_THEME.VALIDATION.REQUIRED');
    } else if (!HEX_COLOR_REGEX.test(value)) {
      errors[key] = this.translateService.instant('SETTINGS_THEME.VALIDATION.INVALID_HEX');
    } else {
      delete errors[key];
    }
    this.validationErrors.set(errors);
    return !errors[key];
  }

  private validateFontSize(value: string): boolean {
    const errors = { ...this.validationErrors() };
    const numeric = Number(value);
    if (!value) {
      errors['baseFontSize'] = this.translateService.instant('SETTINGS_THEME.VALIDATION.REQUIRED');
    } else if (!Number.isFinite(numeric) || numeric < 50 || numeric > 150) {
      errors['baseFontSize'] = this.translateService.instant(
        'SETTINGS_THEME.VALIDATION.FONT_SIZE_RANGE'
      );
    } else {
      delete errors['baseFontSize'];
    }
    this.validationErrors.set(errors);
    return !errors['baseFontSize'];
  }

  private validateAll(): boolean {
    const currentMode = this.activeMode();
    let valid = true;
    for (const mode of ['light', 'dark'] as const) {
      this.activeMode.set(mode);
      for (const field of [
        ...COLOR_FIELDS,
        ...HEADER_COLOR_FIELDS,
        ...SURFACE_COLOR_FIELDS,
        ...SIDEBAR_COLOR_FIELDS,
      ]) {
        valid =
          this.validateField(
            String(field.key),
            field.nestedKey,
            this.getFieldValue(String(field.key), field.nestedKey)
          ) && valid;
      }
      valid = this.validateFontSize(this.getFontSizeNumeric()) && valid;
    }
    this.activeMode.set(currentMode);
    return valid;
  }

  private validationKey(fieldKey: string, nestedKey?: string): string {
    return nestedKey ? `${fieldKey}.${nestedKey}` : fieldKey;
  }

  private cloneTheme(theme: FrontendThemeConfiguration): FrontendThemeConfiguration {
    return {
      light: this.cloneMode(theme.light),
      dark: this.cloneMode(theme.dark),
    };
  }

  private cloneMode(mode: FrontendThemeMode): FrontendThemeMode {
    return {
      ...mode,
      header: { ...mode.header },
      surface: { ...mode.surface },
      sidebar: { ...mode.sidebar },
    };
  }

  private toPatchConfiguration(theme: FrontendThemeConfiguration): PatchFrontendThemeConfiguration {
    return {
      light: this.toPatchMode(theme.light),
      dark: this.toPatchMode(theme.dark),
    };
  }

  private toPatchMode(mode: FrontendThemeMode): PatchFrontendThemeMode {
    return {
      baseFontSize: mode.baseFontSize,
      baseFontStyle: mode.baseFontStyle as PatchFrontendThemeMode['baseFontStyle'],
      primaryColor: mode.primaryColor,
      accentColor: mode.accentColor,
      textColor: mode.textColor,
      textMutedColor: mode.textMutedColor,
      borderColor: mode.borderColor,
      borderStrongColor: mode.borderStrongColor,
      header: { ...mode.header },
      surface: { ...mode.surface },
      sidebar: { ...mode.sidebar },
    };
  }

  private normalizeDescription(value: string | undefined | null): string {
    return (value ?? '').trim();
  }

  private descriptionForPatch(value: string): string | undefined {
    const normalized = this.normalizeDescription(value);
    return normalized === '' ? (null as unknown as string) : normalized;
  }

  private isConflict(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }
    const maybeError = error as { status?: number; response?: { status?: number } };
    return maybeError.status === 409 || maybeError.response?.status === 409;
  }
}
